import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import NavigationBar from 'components/Navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { reservationService, MyReservation } from 'services/reservationService';
import { reviewService, Hashtag } from 'services/reviewService';
import {
  Container,
  Header,
  BackButton,
  Content,
  MainCard,
  FacilitySection,
  FacilityImage,
  FacilityImagePlaceholder,
  FacilityInfo,
  FacilityName,
  FacilityLocation,
  UsageDate,
  RatingSection,
  ReviewSection,
  ImageSection,
  HashtagSection,
  SectionTitle,
  StarRatingContainer,
  StarButton,
  ReviewTextArea,
  ImageUploadSection,
  ImagePreviewContainer,
  ImagePreview,
  RemoveImageButton,
  UploadButton,
  HashtagList,
  HashtagButton,
  SubmitButton
} from './ReviewCreate.styles';

const ReviewCreate: React.FC = () => {
  const navigate = useNavigate();
  const { reservationId } = useParams<{ reservationId: string }>();
  const [reservation, setReservation] = useState<MyReservation | null>(null);
  const [rating, setRating] = useState<number>(0);
  const [reviewText, setReviewText] = useState<string>('');
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [hashtags, setHashtags] = useState<Hashtag[]>([]);
  const [selectedHashtagIds, setSelectedHashtagIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchReservation = async () => {
      if (!reservationId) {
        navigate('/reviews');
        return;
      }

      try {
        const data = await reservationService.getReservationById(parseInt(reservationId));
        setReservation(data);
      } catch (error) {
        console.error('Failed to fetch reservation:', error);
        alert('예약 정보를 불러오는데 실패했습니다.');
        navigate('/reviews');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReservation();
  }, [reservationId, navigate]);

  useEffect(() => {
    const fetchHashtags = async () => {
      try {
        const data = await reviewService.getHashtags();
        setHashtags(data);
      } catch (error) {
        console.error('Failed to fetch hashtags:', error);
      }
    };

    fetchHashtags();
  }, []);

  const handleStarClick = (selectedRating: number) => {
    setRating(selectedRating);
  };

  const handleHashtagToggle = (hashtagId: number) => {
    setSelectedHashtagIds(prev =>
      prev.includes(hashtagId)
        ? prev.filter(id => id !== hashtagId)
        : [...prev, hashtagId]
    );
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages = Array.from(files);
    const totalImages = images.length + newImages.length;

    if (totalImages > 3) {
      alert('최대 3장까지 업로드 가능합니다.');
      return;
    }

    setImages([...images, ...newImages]);

    newImages.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      alert('별점을 선택해주세요.');
      return;
    }

    if (!reviewText.trim()) {
      alert('리뷰를 작성해주세요.');
      return;
    }

    if (!reservationId) {
      alert('예약 정보가 없습니다.');
      return;
    }

    setIsLoading(true);

    try {
      // 이미지가 있으면 먼저 업로드
      let uploadedImages: string[] = [];
      if (images.length > 0) {
        uploadedImages = await reviewService.uploadReviewImages(images);
      }

      // 리뷰 생성
      await reviewService.createReview({
        reservationId: parseInt(reservationId),
        content: reviewText,
        starRating: rating,
        images: uploadedImages,
        hashTagIds: selectedHashtagIds
      });

      alert('리뷰가 등록되었습니다.');
      navigate('/reviews');
    } catch (error: any) {
      console.error('리뷰 등록 실패:', error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('리뷰 등록에 실패했습니다. 다시 시도해주세요.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  if (isLoading) {
    return (
      <>
        <Container>
          <Header>
            <BackButton onClick={() => navigate(-1)}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </BackButton>
            리뷰 작성
          </Header>
          <Content>
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              로딩 중...
            </div>
          </Content>
        </Container>
        <NavigationBar />
      </>
    );
  }

  if (!reservation) {
    return null;
  }

  const locationParts = [
    reservation.facility.building?.name,
    reservation.facility.location
  ].filter(Boolean);

  return (
    <>
      <Container>
        <Header>
          <BackButton onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </BackButton>
          리뷰 작성
        </Header>
        <Content>
          <MainCard>
            <FacilitySection>
              {reservation.facility.imageUrl ? (
                <FacilityImage src={reservation.facility.imageUrl} alt={reservation.facility.name} />
              ) : (
                <FacilityImagePlaceholder>🏢</FacilityImagePlaceholder>
              )}
              <FacilityInfo>
                <FacilityName>{reservation.facility.name}</FacilityName>
                <FacilityLocation>{locationParts.join(' ')}</FacilityLocation>
              </FacilityInfo>
              <UsageDate>이용일자: {formatDate(reservation.startTime)}</UsageDate>
            </FacilitySection>

            <RatingSection>
              <SectionTitle>별점을 선택해주세요.</SectionTitle>
              <StarRatingContainer>
                {[1, 2, 3, 4, 5].map((star) => (
                  <StarButton
                    key={star}
                    filled={star <= rating}
                    onClick={() => handleStarClick(star)}
                  >
                    ★
                  </StarButton>
                ))}
              </StarRatingContainer>
            </RatingSection>

            <ReviewSection>
              <SectionTitle>자세한 리뷰를 남겨주세요.</SectionTitle>
              <ReviewTextArea
                placeholder="리뷰를 입력하세요"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
              />
            </ReviewSection>

            <ImageSection>
              <ImageUploadSection>
                {imagePreviews.length > 0 && (
                  <ImagePreviewContainer>
                    {imagePreviews.map((preview, index) => (
                      <ImagePreview key={index}>
                        <img src={preview} alt={`Preview ${index + 1}`} />
                        <RemoveImageButton onClick={() => handleRemoveImage(index)}>
                          ×
                        </RemoveImageButton>
                      </ImagePreview>
                    ))}
                  </ImagePreviewContainer>
                )}
                {images.length < 3 && (
                  <UploadButton>
                    📷 사진 첨부하기
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                    />
                  </UploadButton>
                )}
              </ImageUploadSection>
            </ImageSection>

            <HashtagSection>
              <SectionTitle>태그를 선택해주세요. (다중 선택 가능)</SectionTitle>
              <HashtagList>
                {hashtags.map((hashtag) => (
                  <HashtagButton
                    key={hashtag.id}
                    selected={selectedHashtagIds.includes(hashtag.id)}
                    onClick={() => handleHashtagToggle(hashtag.id)}
                    type="button"
                  >
                    #{hashtag.name}
                  </HashtagButton>
                ))}
              </HashtagList>
            </HashtagSection>
          </MainCard>

          <SubmitButton onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? '등록 중...' : '올리기'}
          </SubmitButton>
        </Content>
      </Container>
      <NavigationBar />
    </>
  );
};

export default ReviewCreate;
